import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { BACKEND_URL } from '../App';
import { toast } from 'sonner';
import {
  GraduationCap, CheckCircle2, ChevronDown, ArrowLeft, Shield, Clock,
  User, Calendar, TrendingUp, Star, AlertTriangle, Award, Lock, FileCheck, X
} from 'lucide-react';

function api(path, opts = {}) {
  const token = localStorage.getItem('ps_token');
  return fetch(`${BACKEND_URL}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}), ...opts.headers } }).then(r => { if (!r.ok) throw new Error(); return r.json(); });
}

const CLEARANCE_COLORS = { 'Not Cleared': 'badge-critical', 'Observing Only': 'badge-draft', 'Supervised': 'badge-improving', 'Cleared': 'badge-active' };

export default function MyTraining() {
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const nav = (v, id = null) => { setView(v); setSelectedId(id); };
  const location = useLocation();

  // Deep-link support: /my-training?profile=<id> opens that profile directly
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get('profile');
    if (pid) { setSelectedId(pid); setView('profile'); }
  }, [location.search]);

  return (
    <div className="page-container" data-testid="my-training">
      {view === 'list' && <ProfileList navigate={nav} />}
      {view === 'profile' && <TrainingProfile id={selectedId} navigate={nav} />}
      {view === 'review' && <ReviewForm profileId={selectedId} navigate={nav} />}
    </div>
  );
}

// ============================================================
// PROFILE LIST
// ============================================================
function ProfileList({ navigate }) {
  const [profiles, setProfiles] = useState([]);
  useEffect(() => { api('/api/training/profiles').then(d => setProfiles(d.profiles || [])).catch(() => {}); }, []);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">My Training</h1>
        <p className="text-sm text-muted-foreground mt-1">Staff training pathways and 90 day trials</p>
      </div>
      {profiles.length === 0 ? (
        <div className="ps-card-static p-8 text-center">
          <GraduationCap size={40} className="mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm font-semibold mb-1">No training profiles yet</p>
          <p className="text-xs text-muted-foreground">When a candidate is hired in the Recruitment Hub, their training profile is created automatically.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map(p => (
            <button key={p.id} onClick={() => navigate('profile', p.id)} className="w-full ps-card p-5 text-left flex items-center gap-4">
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
                {(p.staff_name || '?').split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">{p.staff_name}</p>
                <p className="text-xs text-muted-foreground">{p.role} · Started {p.start_date}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold text-primary">{p.progress_pct}%</p>
                <p className="text-xs text-muted-foreground">{p.tasks_done}/{p.tasks_total} tasks</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TRAINING PROFILE
// ============================================================
function TrainingProfile({ id, navigate }) {
  const [profile, setProfile] = useState(null);
  const [phases, setPhases] = useState([]);
  const [clearanceCats, setClearanceCats] = useState([]);
  const [clearanceLevels, setClearanceLevels] = useState([]);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const load = useCallback(() => {
    if (id) api(`/api/training/profiles/${id}`).then(setProfile).catch(() => {});
    api('/api/training/phases').then(d => { setPhases(d.phases || []); setClearanceCats(d.clearance_categories || []); setClearanceLevels(d.clearance_levels || []); }).catch(() => {});
  }, [id]);
  useEffect(load, [load]);

  const toggleTask = async (taskKey, completed) => {
    await api(`/api/training/profiles/${id}/task`, { method: 'PATCH', body: JSON.stringify({ task_key: taskKey, completed, signed_off_by: 'Manager' }) });
    toast.success(completed ? 'Task signed off' : 'Task unchecked');
    load();
  };

  const updateClearance = async (category, level) => {
    await api(`/api/training/profiles/${id}/clearance`, { method: 'PATCH', body: JSON.stringify({ category, level, approved_by: 'Manager' }) });
    toast.success(`Clearance updated: ${level}`);
    load();
  };

  const updateTrialStage = async (stage) => {
    await api(`/api/training/profiles/${id}/trial-stage`, { method: 'PATCH', body: JSON.stringify({ stage }) });
    toast.success(`Trial stage: ${stage}`);
    load();
  };

  if (!profile) return <div className="h-40 bg-muted rounded-2xl animate-skeleton" />;

  const today = new Date().toISOString().split('T')[0];
  const review30Due = profile.review_30_date && profile.review_30_date <= today && !profile.review_30_complete;
  const review60Due = profile.review_60_date && profile.review_60_date <= today && !profile.review_60_complete;
  const review90Due = profile.review_90_date && profile.review_90_date <= today && !profile.review_90_complete;

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('list')}><ArrowLeft size={16} /> Back</button>
      
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
          {(profile.staff_name || '?').split(' ').map(n => n[0]).join('')}
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">{profile.staff_name}</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
            <span className="flex items-center gap-1"><User size={12} />{profile.role}</span>
            <span className="flex items-center gap-1"><Calendar size={12} />Started {profile.start_date}</span>
            {profile.manager && <span className="flex items-center gap-1"><Shield size={12} />Manager: {profile.manager}</span>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">{profile.progress_pct}%</p>
          <p className="text-xs text-muted-foreground">{profile.tasks_done}/{profile.tasks_total}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-3 bg-muted rounded-full overflow-hidden mb-6">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${profile.progress_pct}%`, background: 'hsl(var(--primary))' }} />
      </div>

      {/* Review alerts */}
      {(review30Due || review60Due || review90Due) && (
        <div className="bg-[hsl(38_90%_96%)] border border-[hsl(38_60%_85%)] rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertTriangle size={18} className="text-[hsl(38_80%_45%)] flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold">Review due</p>
            <p className="text-xs text-muted-foreground">
              {review30Due && '30 day review is due. '}
              {review60Due && '60 day review is due. '}
              {review90Due && '90 day review is due. '}
            </p>
          </div>
          <button onClick={() => navigate('review', id)} className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>Complete Review</button>
        </div>
      )}

      {/* Training Phases — collapsible */}
      <div className="mb-6">
        <button onClick={() => setExpandedSection(expandedSection === 'phases' ? null : 'phases')} className="flex items-center justify-between w-full mb-3 text-left">
          <div className="flex items-center gap-2"><GraduationCap size={16} className="text-primary" /><h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Training Phases</h2></div>
          <ChevronDown size={16} className={`text-muted-foreground transition-transform ${expandedSection === 'phases' ? 'rotate-180' : ''}`} />
        </button>
        {expandedSection === 'phases' && (
        <div className="space-y-2">
          {phases.map((phase, pi) => {
            const pp = profile.phase_progress?.[pi];
            const isDone = pp && pp.done === pp.total;
            const isOpen = expandedPhase === pi;
            return (
              <div key={pi} className="ps-card-static overflow-hidden">
                <button onClick={() => setExpandedPhase(isOpen ? null : pi)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/20">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isDone ? 'bg-[hsl(var(--success))] text-white' : 'bg-muted text-muted-foreground'}`}>
                    {isDone ? <CheckCircle2 size={14} /> : pi + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{phase.title}</p>
                    <p className="text-xs text-muted-foreground">Owner: {phase.owner} · {pp?.done || 0}/{pp?.total || 0} tasks</p>
                  </div>
                  <ChevronDown size={14} className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-border/50 pt-3 space-y-1">
                    {phase.tasks.map(task => {
                      const status = profile.task_status?.[task.key];
                      const done = status?.completed;
                      return (
                        <button key={task.key} onClick={() => toggleTask(task.key, !done)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${done ? 'bg-[hsl(142_40%_97%)]' : 'hover:bg-muted/30'}`}>
                          <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${done ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white' : 'border-input'}`}>
                            {done && <CheckCircle2 size={12} />}
                          </span>
                          <span className={done ? 'line-through opacity-50' : ''}>{task.label}</span>
                          {done && status.signed_off_by && <span className="ml-auto text-xs text-muted-foreground">{status.signed_off_by}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Clearance Levels — collapsible */}
      <div className="mb-6">
        <button onClick={() => setExpandedSection(expandedSection === 'clearance' ? null : 'clearance')} className="flex items-center justify-between w-full mb-3 text-left">
          <div className="flex items-center gap-2"><Lock size={16} className="text-primary" /><h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Clearance Levels</h2></div>
          <ChevronDown size={16} className={`text-muted-foreground transition-transform ${expandedSection === 'clearance' ? 'rotate-180' : ''}`} />
        </button>
        {expandedSection === 'clearance' && (
        <div className="space-y-2">
          {clearanceCats.map(cat => {
            const cl = profile.clearances?.[cat.key] || {};
            return (
              <div key={cat.key} className="ps-card-static p-4 flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium flex-1 min-w-0">{cat.label}</span>
                <div className="flex gap-1">
                  {clearanceLevels.map(level => (
                    <button key={level} onClick={() => updateClearance(cat.key, level)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${cl.level === level ? CLEARANCE_COLORS[level] : 'bg-muted/50 text-muted-foreground hover:bg-muted'}`}>
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground italic px-1">No staff member can be marked as Cleared unless a manager has observed them performing the task to standard.</p>
        </div>
        )}
      </div>

      {/* 90-Day Trial — collapsible */}
      <div className="mb-6">
        <button onClick={() => setExpandedSection(expandedSection === 'trial' ? null : 'trial')} className="flex items-center justify-between w-full mb-3 text-left">
          <div className="flex items-center gap-2"><Clock size={16} className="text-primary" /><h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">90 Day Trial</h2></div>
          <ChevronDown size={16} className={`text-muted-foreground transition-transform ${expandedSection === 'trial' ? 'rotate-180' : ''}`} />
        </button>
        {expandedSection === 'trial' && (
        <div className="ps-card-static p-5">
          <div className="grid grid-cols-3 gap-3 mb-4 text-center">
            <div className={`p-3 rounded-xl ${profile.review_30_complete ? 'bg-[hsl(142_40%_95%)]' : review30Due ? 'bg-[hsl(38_90%_95%)]' : 'bg-muted/30'}`}>
              <p className="text-xs font-semibold">30 Day Review</p>
              <p className="text-xs text-muted-foreground">{profile.review_30_date}</p>
              <p className={`text-xs font-semibold mt-1 ${profile.review_30_complete ? 'text-[hsl(142_34%_34%)]' : review30Due ? 'text-[hsl(38_80%_40%)]' : ''}`}>{profile.review_30_complete ? 'Complete' : review30Due ? 'Due' : 'Upcoming'}</p>
            </div>
            <div className={`p-3 rounded-xl ${profile.review_60_complete ? 'bg-[hsl(142_40%_95%)]' : review60Due ? 'bg-[hsl(38_90%_95%)]' : 'bg-muted/30'}`}>
              <p className="text-xs font-semibold">60 Day Review</p>
              <p className="text-xs text-muted-foreground">{profile.review_60_date}</p>
              <p className={`text-xs font-semibold mt-1 ${profile.review_60_complete ? 'text-[hsl(142_34%_34%)]' : review60Due ? 'text-[hsl(38_80%_40%)]' : ''}`}>{profile.review_60_complete ? 'Complete' : review60Due ? 'Due' : 'Upcoming'}</p>
            </div>
            <div className={`p-3 rounded-xl ${profile.review_90_complete ? 'bg-[hsl(142_40%_95%)]' : review90Due ? 'bg-[hsl(38_90%_95%)]' : 'bg-muted/30'}`}>
              <p className="text-xs font-semibold">90 Day Review</p>
              <p className="text-xs text-muted-foreground">{profile.review_90_date}</p>
              <p className={`text-xs font-semibold mt-1 ${profile.review_90_complete ? 'text-[hsl(142_34%_34%)]' : review90Due ? 'text-[hsl(38_80%_40%)]' : ''}`}>{profile.review_90_complete ? 'Complete' : review90Due ? 'Due' : 'Upcoming'}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate('review', id)} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
              <FileCheck size={14} className="inline mr-1" /> Complete Review
            </button>
          </div>
          {profile.final_decision && (
            <div className="mt-4 p-3 rounded-xl bg-muted/50">
              <p className="text-xs font-semibold">Final Decision: {profile.final_decision}</p>
              {profile.final_decision_reason && <p className="text-xs text-muted-foreground mt-1">{profile.final_decision_reason}</p>}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// REVIEW FORM (30/60/90 day)
// ============================================================
function ReviewForm({ profileId, navigate }) {
  const [categories, setCategories] = useState([]);
  const [finalDecisions, setFinalDecisions] = useState([]);
  const [reviewType, setReviewType] = useState('30-day');
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState('');
  const [concerns, setConcerns] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { api('/api/training/review-categories').then(d => { setCategories(d.categories || []); setFinalDecisions(d.final_decisions || []); }).catch(() => {}); }, []);

  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const maxScore = categories.length * 5;
  const pct = maxScore > 0 ? Math.round(totalScore / maxScore * 100) : 0;

  const save = async () => {
    setSaving(true);
    try {
      await api('/api/training/reviews', { method: 'POST', body: JSON.stringify({ profile_id: profileId, review_type: reviewType, scores, focus_notes: notes, concerns, recommendation }) });
      toast.success(`${reviewType} review saved`);
      navigate('profile', profileId);
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const FOCUS = {
    '30-day': ['Is the person reliable?', 'Are they respectful?', 'Are they learning?', 'Are they emotionally suitable for this work?', 'Are there any early concerns?'],
    '60-day': ['Are they becoming more independent?', 'Are they consistent?', 'Can they follow procedures with less prompting?', 'Are they showing initiative without overstepping?', 'Are they safe to increase responsibilities?'],
    '90-day': ['Can this person protect the Pure Souls reputation?', 'Can this person be trusted with grieving families?', 'Can this person follow procedures independently?', 'Does this person naturally care?', 'Should this person become permanent, continue casual, extend trial, or finish employment?'],
  };

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('profile', profileId)}><ArrowLeft size={16} /> Back</button>
      <h1 className="text-xl font-bold mb-2">Performance Review</h1>
      <div className="flex items-center gap-3 mb-6">
        <span className="text-lg font-bold">{pct}%</span>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${pct >= 80 ? 'badge-active' : pct >= 60 ? 'badge-improving' : 'badge-draft'}`}>{pct >= 80 ? 'Strong' : pct >= 60 ? 'Satisfactory' : 'Needs attention'}</span>
      </div>

      {/* Review type selector */}
      <div className="flex gap-1 p-1 rounded-xl bg-muted mb-6 w-fit">
        {['30-day', '60-day', '90-day'].map(t => (
          <button key={t} onClick={() => setReviewType(t)} className={`px-4 py-2 rounded-lg text-xs font-medium ${reviewType === t ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>{t}</button>
        ))}
      </div>

      {/* Focus questions */}
      <div className="ps-card-static p-4 mb-6">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Focus for this review</p>
        <div className="space-y-1">
          {(FOCUS[reviewType] || []).map((q, i) => (
            <p key={i} className="text-xs p-2 rounded-lg bg-muted/20"><span className="font-semibold text-primary">{i+1}.</span> {q}</p>
          ))}
        </div>
      </div>

      {/* Score each category */}
      <div className="ps-card-static p-6 mb-6">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Score Each Category (1 to 5)</p>
        <div className="space-y-3">
          {categories.map(cat => (
            <div key={cat} className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-medium w-40 flex-shrink-0">{cat}</span>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(v => (
                  <button key={v} onClick={() => setScores(s => ({ ...s, [cat]: v }))} className={`w-8 h-8 rounded-lg text-xs font-bold border transition-colors ${scores[cat] === v ? 'bg-primary text-white border-primary' : 'border-input hover:border-primary'}`}>{v}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="ps-card-static p-6 mb-6">
        <div className="rec-field"><label className="rec-label">Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} className="rec-input rec-textarea" rows={3} placeholder="General observations..." /></div>
        <div className="rec-field mt-3"><label className="rec-label">Concerns</label><textarea value={concerns} onChange={e => setConcerns(e.target.value)} className="rec-input rec-textarea" rows={2} placeholder="Any concerns..." /></div>
        {reviewType === '90-day' && (
          <div className="rec-field mt-3"><label className="rec-label">Final Decision</label>
            <select value={recommendation} onChange={e => setRecommendation(e.target.value)} className="rec-input">
              <option value="">Select...</option>
              {finalDecisions.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        )}
        <button onClick={save} disabled={saving} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>{saving ? 'Saving...' : `Save ${reviewType} Review`}</button>
      </div>
    </div>
  );
}
